// Pure helpers for the Outlook Web (confidential authorization-code) connect
// flow. No MSAL. The browser builds the /authorize URL and navigates; the
// refresh token is minted server-side by outlook-oauth-exchange.

const AUTHORIZE_BASE =
  'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Random URL-safe string (43 chars from 32 random bytes). CSRF state / verifier. */
function randomUrlSafe(numBytes = 32): string {
  const bytes = new Uint8Array(numBytes);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export function generateState(): string {
  return randomUrlSafe();
}

/** PKCE S256: verifier is a random URL-safe string; challenge = base64url(SHA256(verifier)). */
export async function generatePkcePair(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  const codeVerifier = randomUrlSafe();
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(codeVerifier),
  );
  const codeChallenge = base64UrlEncode(new Uint8Array(digest));
  return { codeVerifier, codeChallenge };
}

/**
 * Build the Microsoft authorize URL for the Web flow.
 *
 * PR #470 prompt rules (carry forward):
 *   - first-time Connect  → prompt='select_account'
 *   - Reconnect           → prompt='login'
 *   - NEVER 'consent'     → re-triggers the Entra admin-consent workflow on
 *                            already-consented tenants.
 * Same client_id as the SPA flow so existing tenant admin consent carries over.
 */
export function buildAuthorizeUrl(opts: {
  clientId: string;
  redirectUri: string;
  scope: string;
  state: string;
  codeChallenge: string;
  prompt: 'select_account' | 'login';
}): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: opts.clientId,
    redirect_uri: opts.redirectUri,
    response_mode: 'query',
    scope: opts.scope,
    state: opts.state,
    code_challenge: opts.codeChallenge,
    code_challenge_method: 'S256',
    prompt: opts.prompt,
  });
  return `${AUTHORIZE_BASE}?${params.toString()}`;
}
